import { useEffect, useState } from "react";
import { Button, Drawer, DrawerBody, DrawerFooter, DrawerHeader, Input, Textarea } from "@/components";
import type { ApiDeviceProfile } from "../../_types/device-profiles.types";
import styles from "../create-policy-drawer/CreatePolicyDrawer.module.scss";

type EditPolicyDrawerProps = {
  isOpen: boolean;
  profile: ApiDeviceProfile | null;
  onClose: () => void;
  onSave: (profile: ApiDeviceProfile) => void;
};

export const EditPolicyDrawer = ({ isOpen, profile, onClose, onSave }: EditPolicyDrawerProps) => {
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [configText, setConfigText] = useState("{}");
  const [nameError, setNameError] = useState("");
  const [configError, setConfigError] = useState("");
  const [submitError, setSubmitError] = useState<string | null>(null);

  useEffect(() => {
    if (!isOpen || !profile) return;
    setName(profile.name);
    setDescription(profile.description ?? "");
    setConfigText(JSON.stringify(profile.config, null, 2));
    setNameError("");
    setConfigError("");
    setSubmitError(null);
  }, [isOpen, profile]);

  const handleClose = () => {
    onClose();
  };

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!profile) return;
    if (name.trim().length < 1) {
      setNameError("Name is required");
      return;
    }
    setNameError("");
    let config: Record<string, unknown>;
    try {
      config = JSON.parse(configText) as Record<string, unknown>;
    } catch {
      setConfigError("Invalid JSON");
      return;
    }
    setConfigError("");
    setSubmitError(null);
    onSave({
      ...profile,
      name: name.trim(),
      description: description.trim() || null,
      config,
      configVersion: profile.configVersion + 1,
      updatedAt: Date.now(),
    });
    onClose();
  };

  return (
    <Drawer isOpen={isOpen} onClose={handleClose} ariaLabel="Edit device profile (prototype)">
      <DrawerHeader title="Edit profile (Prototype)" onClose={handleClose} />
      <form className={styles["comp__stack"]} onSubmit={handleSubmit}>
        <DrawerBody>
          <div className={styles["comp"]}>
            <p className={styles["comp__hint"]}>
              Preview only — edits stay in memory until refresh and never call the API.
            </p>
            <div className={styles["comp__fields"]}>
              <Input
                id="edit-profile-name"
                label="Name"
                value={name}
                placeholder="e.g. Kiosk — reception"
                required
                error={nameError}
                onChange={(v) => {
                  setName(v);
                  setNameError("");
                  setSubmitError(null);
                }}
              />

              <Input
                id="edit-profile-description"
                label="Description"
                value={description}
                placeholder="Optional"
                onChange={(v) => setDescription(v)}
              />

              <Textarea
                id="edit-profile-config"
                className={styles["comp__textarea"]}
                label="Config (JSON)"
                value={configText}
                onChange={(next) => {
                  setConfigText(next);
                  setConfigError("");
                  setSubmitError(null);
                }}
                rows={14}
                spellCheck={false}
                error={configError}
              />
            </div>

            {submitError ? <div className={styles["comp__error"]}>{submitError}</div> : null}
          </div>
        </DrawerBody>
        <DrawerFooter>
          <Button type="button" variant="secondary" onClick={handleClose}>
            Cancel
          </Button>
          <Button type="submit" variant="primary">
            Save changes
          </Button>
        </DrawerFooter>
      </form>
    </Drawer>
  );
};
